import { useCallback, useState } from 'react';
import config from '../config';
import { Error as ApiError, MoveListsResult } from '../types';
import prepareHeaders from '../utilities/prepare-headers';
import { useAuth } from './auth';

const useSchemaSync = () => {
  const { token } = useAuth();

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleError = (error: Error) => {
    setPending(false);

    if (error.name === 'AbortError') {
      return;
    }

    setError(error);
  };

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Move lists to a scope, or release them from their scope with null
   *
   * A move that's refused still returns its plan (with blockedBy set), since
   * the plan says why
   */
  const moveLists = useCallback(
    async (
      selection: { lists: string[] } | { fromScope: string },
      scope: string | null,
      options: { dryRun?: boolean } = {}
    ): Promise<MoveListsResult | void> => {
      setPending(true);
      setError(null);

      return fetch(
        `${config.api.url}/sync-schema/move${options.dryRun ? '?dryRun=true' : ''}`,
        {
          method: 'POST',
          headers: prepareHeaders(token),
          body: JSON.stringify({ ...selection, scope }),
        }
      )
        .then(async response => {
          setPending(false);

          const body = await response.json();

          if (!response.ok && !Array.isArray(body?.changes)) {
            setError(new Error((body as ApiError).message));
            return;
          }

          return body as MoveListsResult;
        })
        .catch(handleError);
    },
    [token]
  );

  return {
    pending,
    error,
    resetError,
    moveLists,
  };
};

export default useSchemaSync;
